import Swiper from 'swiper';
import SectionTitleReveal from '../common/section-title-reveal';
import 'swiper/css';
import 'swiper/css/pagination';
import { Pagination, Navigation } from 'swiper/modules';
import { bindSwiperNavigationA11y, syncSwiperNavigationA11y } from '../common/syncSwiperNavigationA11y';

export default class ExpertisesAreasList {

    constructor() {
        this.sections = document.querySelectorAll('.block-expertises-areas-list');
        this.instances = [];
        this.itemsBySection = new Map();
        this.filteredItemsBySection = new Map();
        this.handleResize = this.debounce(this.rebuild.bind(this), 300);

        this.sections.forEach(section => {
            this.itemsBySection.set(
                section,
                Array.from(section.querySelectorAll('[data-expertise-item]')),
            );
        });

        this.ready();
        this.bindSearch();
        window.addEventListener('resize', this.handleResize);
    }

    debounce(fn, delay) {
        let timer;

        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => fn.apply(this, args), delay);
        };
    }

    rebuild() {
        this.instances.forEach(instance => instance.main?.destroy(true, true));
        this.instances = [];
        this.ready();
    }

    regroupSlides(slider, items = null) {
        if (!slider.hasAttribute('data-mobile-rows')) return;

        const wrapper = slider.querySelector('.swiper-wrapper');
        const section = slider.closest('.block-expertises-areas-list');
        const slideItems = items
            ?? this.filteredItemsBySection.get(section)
            ?? this.itemsBySection.get(section)
            ?? [];
        const itemsPerSlide = window.innerWidth < 1024
            ? Number(slider.dataset.mobileRows)
            : 16;

        if (!wrapper) return;

        wrapper.replaceChildren();

        for (let index = 0; index < slideItems.length; index += itemsPerSlide) {
            const slide = document.createElement('div');
            const grid = document.createElement('div');
            const currentSlideItems = slideItems.slice(index, index + itemsPerSlide);

            slide.className = 'swiper-slide';
            grid.className = 'grid grid-cols-1 gap-7 lg:grid-cols-4 lg:gap-0';

            currentSlideItems.forEach(item => {
                item.classList.remove('hidden');
                grid.appendChild(item);
            });
            slide.appendChild(grid);
            wrapper.appendChild(slide);
        }
    }

    ready() {
        if (this.sections.length === 0) return;

        this.sections.forEach(section => {
            const slider = section.querySelector('.swiper');
            if (!slider) return;

            this.regroupSlides(slider);

            const pagination = section.querySelector('.swiper-pagination');
            const navigationNext = section.querySelector('.slider-button-next');
            const navigationPrev = section.querySelector('.slider-button-prev');

            const swiperInstance = new Swiper(slider, {
                modules: [Pagination, Navigation],
                slidesPerView: 1,
                pagination: pagination ? {
                    el: pagination,
                    clickable: true,
                } : false,
                navigation: navigationNext && navigationPrev ? {
                    nextEl: navigationNext,
                    prevEl: navigationPrev,
                } : false,
            });

            bindSwiperNavigationA11y(swiperInstance);

            this.instances.push({ section, main: swiperInstance });
        });
    }

    bindSearch() {
        this.sections.forEach(section => {
            const form = section.querySelector('.formFilterSearch');
            const input = form?.querySelector('input');
            const clearButton = form?.querySelector('.search-clear');
            const emptyMessage = section.querySelector('.search-empty');
            const results = section.querySelector('.expertises-results');
            const getSwiper = () => this.instances.find(instance => instance.section === section)?.main;
            if (!form || !input) return;

            const resetSwiperPagination = () => {
                const swiper = getSwiper();

                if (!swiper) return;

                // Drop the previous slide position before recalculating the new result set.
                swiper.setTranslate(0);
                swiper.recalcSlides();
                swiper.update();
                swiper.slideTo(0, 0);
                swiper.pagination?.render();
                swiper.pagination?.update();
                swiper.navigation?.update();

                return swiper;
            };

            const reset = () => {
                input.value = '';
                input.setCustomValidity('');
                this.filteredItemsBySection.delete(section);
                this.regroupSlides(section.querySelector('.swiper'), this.itemsBySection.get(section));
                emptyMessage?.classList.add('hidden');
                results?.classList.remove('hidden');
                clearButton?.classList.add('hidden');
                clearButton?.classList.remove('flex');
                const swiper = resetSwiperPagination();
                syncSwiperNavigationA11y(swiper);
            };

            input.addEventListener('input', () => {
                input.setCustomValidity('');
                const hasValue = input.value.length > 0;
                clearButton?.classList.toggle('hidden', !hasValue);
                clearButton?.classList.toggle('flex', hasValue);
            });

            clearButton?.addEventListener('click', () => {
                reset();
                input.focus();
            });

            form.addEventListener('submit', (e) => {
                e.preventDefault();

                const term = input.value.toLowerCase().trim();

                if (term.length === 0) {
                    reset();
                    return;
                }

                if (term.length < 3) {
                    input.setCustomValidity(form.dataset.minLengthMessage);
                    input.reportValidity();
                    return;
                }

                input.setCustomValidity('');

                const matchedItems = this.itemsBySection.get(section).filter(item => (
                    item.textContent.toLowerCase().includes(term)
                ));
                const slider = section.querySelector('.swiper');

                this.filteredItemsBySection.set(section, matchedItems);
                this.regroupSlides(slider, matchedItems);

                const hasResults = matchedItems.length > 0;
                emptyMessage?.classList.toggle('hidden', hasResults);
                results?.classList.toggle('hidden', !hasResults);

                const updatedSwiper = resetSwiperPagination();
                const visibleSlideCount = slider.querySelectorAll('.swiper-slide').length;

                if (visibleSlideCount <= 1 && updatedSwiper?.navigation) {
                    [updatedSwiper.navigation.prevEl, updatedSwiper.navigation.nextEl].forEach(button => {
                        if (!button) return;
                        button.disabled = true;
                        button.tabIndex = -1;
                        button.setAttribute('aria-disabled', 'true');
                    });
                } else {
                    syncSwiperNavigationA11y(updatedSwiper);
                }
            });
        });
    }
}

new SectionTitleReveal('.block-expertises-areas-list');

if (document.querySelector('.block-expertises-areas-list')) {
    new ExpertisesAreasList();
}
